// Pure camera math for the infinite board.
// Screen = world * z + (x, y)  ⇔  CSS transform: translate(x, y) scale(z).

export interface Camera {
  x: number;
  y: number;
  z: number;
}

export const MIN_ZOOM = 0.35;
export const MAX_ZOOM = 2.5;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

export const screenToWorld = (c: Camera, sx: number, sy: number) => ({
  x: (sx - c.x) / c.z,
  y: (sy - c.y) / c.z,
});

export const worldToScreen = (c: Camera, wx: number, wy: number) => ({
  x: wx * c.z + c.x,
  y: wy * c.z + c.y,
});

export const panBy = (c: Camera, dx: number, dy: number): Camera => ({
  ...c,
  x: c.x + dx,
  y: c.y + dy,
});

/** Zoom by `factor`, keeping the screen point (sx, sy) fixed. */
export function zoomAt(c: Camera, sx: number, sy: number, factor: number): Camera {
  const z = clamp(c.z * factor, MIN_ZOOM, MAX_ZOOM);
  const k = z / c.z;
  return { x: sx - (sx - c.x) * k, y: sy - (sy - c.y) * k, z };
}

/** Camera that shows world point (wx, wy) at screen point (sx, sy) at zoom z. */
export const cameraOver = (wx: number, wy: number, sx: number, sy: number, z: number): Camera => ({
  x: sx - wx * z,
  y: sy - wy * z,
  z,
});

/** Two-pointer pinch: previous and current positions of both pointers → new camera. */
export function pinch(
  c: Camera,
  a0: { x: number; y: number },
  b0: { x: number; y: number },
  a1: { x: number; y: number },
  b1: { x: number; y: number }
): Camera {
  const d0 = Math.hypot(b0.x - a0.x, b0.y - a0.y) || 1;
  const d1 = Math.hypot(b1.x - a1.x, b1.y - a1.y) || 1;
  const m0 = { x: (a0.x + b0.x) / 2, y: (a0.y + b0.y) / 2 };
  const m1 = { x: (a1.x + b1.x) / 2, y: (a1.y + b1.y) / 2 };
  const zoomed = zoomAt(c, m0.x, m0.y, d1 / d0);
  return panBy(zoomed, m1.x - m0.x, m1.y - m0.y);
}
